import React, { useState, useEffect, useRef } from 'react';
import { 
  Modal, Form, Button, Table, InputNumber, Space, 
  notification, Select, Input, Spin, Typography, Divider,
  Checkbox, Radio, Tooltip
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined,
  WarningOutlined, ShoppingCartOutlined, 
  PercentageOutlined, PoundOutlined, QuestionCircleOutlined
} from '@ant-design/icons';
import ordersService from '../../../../services/ordersService';
import productService from '../../../../services/productService';
import { formatPrice } from '../../../../utils/orderUtils';
import userRoleUtils from '../../../../utils/userRoleUtils';
import { cleaningQuantityStep } from '../../../../utils/cleaningQuantity';
import {
  calculateOrderEditTotals,
  resolveOrderShipping,
  resolvePersistedDiscount,
} from '../../../../utils/orderEditTotals';

const { Option } = Select;
const { Text } = Typography;

// "Collection Only" product supports fractional quantities (hours)
const COLLECTION_ONLY_PRODUCT_ID = 106;

const getItemNameForQtyRules = (item) =>
  (
    item?.order_products_name ||
    item?.product_name ||
    item?.product_title ||
    item?.name ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();

const isCleaningQtyItem = (item) => {
  const name = getItemNameForQtyRules(item);
  const productId = item?.product_id ?? item?.id;
  const categoryId = item?.category_id;

  return (
    (productId != null && parseInt(productId, 10) === COLLECTION_ONLY_PRODUCT_ID) ||
    name === 'collection only' ||
    name.includes('collection only') ||
    name.includes('cleaning') ||
    categoryId === 'cleaning' || 
    name.includes('Cleaning')
  );
};

const clampAndNormalizeQty = (qty, { allowDecimal }) => {
  if (qty == null || Number.isNaN(qty)) return allowDecimal ? 0.05 : 1;

  if (!allowDecimal) {
    const intVal = parseInt(qty, 10);
    return Number.isFinite(intVal) && intVal >= 1 ? intVal : 1;
  }

  const floatVal = parseFloat(qty);
  // Normalize to 2dp to avoid 0.15000000000002 etc.
  const normalized = parseFloat(floatVal.toFixed(2));
  return Number.isFinite(normalized) && normalized >= 0.05 ? normalized : 0.05;
};

// Check if user is admin
const isAdmin = () => {
  return userRoleUtils.isAdminOrImpersonating();
};

/**
 * Modal for editing order items (Admin only)
 */
const OrderItemsEditModal = ({ visible, orderId, onCancel, onSuccess }) => {
  const orderRequestRef = useRef(0);
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscountSection, setShowDiscountSection] = useState(false);
  const [discountType, setDiscountType] = useState('fixed'); // 'fixed' or 'percentage'
  const [discountValue, setDiscountValue] = useState(0);
  const [orderNote, setOrderNote] = useState('');
  const [newItem, setNewItem] = useState({
    product_id: null,
    product_name: '',
    qty: 1,
    price: 0
  });
  
  const [showExtraItem, setShowExtraItem] = useState(false);
  const [extraItem, setExtraItem] = useState({
    name: '',
    qty: 1,
    price: 0,
    add_to_products: false
  });

  useEffect(() => {
    if (visible && orderId) {
      setOrderNote('');
      (async () => {
        const loadedOrder = await fetchOrderData();
        await fetchProducts(loadedOrder?.user_id);
      })();
    }
    
    // Reset state when modal closes
    if (!visible) {
      orderRequestRef.current += 1;
      setOrder(null);
      setOrderItems([]);
      setHasChanges(false);
      setOrderNote('');
      setNewItem({
        product_id: null,
        product_name: '',
        qty: 1,
        price: 0
      });
      setShowExtraItem(false);
      setExtraItem({
        name: '',
        qty: 1,
        price: 0,
        add_to_products: false
      });
    }
  }, [visible, orderId]);

  // Fetch order data
  const fetchOrderData = async () => {
    const requestId = ++orderRequestRef.current;

    try {
      setIsLoading(true);
      const response = await ordersService.getOrderByIdAdmin(orderId);

      if (requestId !== orderRequestRef.current) {
        return null;
      }
      
      if (response && response.order) {
        setOrder(response.order);
        
        // Format order items with unique keys
        const items = response.order.order_products || [];
        const formattedItems = items.map(item => ({
          ...item,
          id: item.id, // Explicitly preserve the id
          key: `${item.id || Math.random()}`,
          product_id: item.product_id,
          product_name: item.order_products_name,
          order_products_name: item.order_products_name,
          qty: clampAndNormalizeQty(item.order_products_qty, { allowDecimal: isCleaningQtyItem(item) }),
          order_products_qty: clampAndNormalizeQty(item.order_products_qty, { allowDecimal: isCleaningQtyItem(item) }),
          price: parseFloat(item.order_products_price),
          order_products_price: parseFloat(item.order_products_price),
          is_extra: Boolean(item.is_extra || item.order_item_type === 'extra')
        }));
        setOrderItems(formattedItems);
        
        // Initialize discount section if there's a discount applied
        const persistedDiscount = resolvePersistedDiscount(response.order, formattedItems);
        setShowDiscountSection(persistedDiscount.enabled);
        setDiscountValue(persistedDiscount.value);
        setDiscountType(persistedDiscount.type);
        
        // Always replace the note for the requested order, including an empty
        // note, so a previous order's admin note can never leak into this one.
        setOrderNote(response.order.admin_notes || '');
        return response.order;
      }
      return null;
    } catch (error) {
      if (requestId !== orderRequestRef.current) {
        return null;
      }
      notification.error({
        message: 'Error loading order',
        description: error.message || 'Failed to load order data'
      });
    } finally {
      if (requestId === orderRequestRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Fetch available products
  const fetchProducts = async (userId) => {
    try {
      const response = await productService.getAllProducts({ limit: 300, status: 1, user_id: userId, order_id: orderId });
      if (response && response.data) {
        setAvailableProducts(response.data.data || []);

        //also add packs to avcailable products frpm response.data.packs and map pack.name to product.product_title
        const packs = response.packs || [];
        const formattedPacks = packs.map(pack => ({
          ...pack,
          product_title: pack.name,
          product_price: pack.price
        }));
        console.log('Formatted Packs:', formattedPacks);
        //merge to available products
        setAvailableProducts(prevProducts => [...prevProducts, ...formattedPacks]);
        
      }
    } catch (error) {
      notification.error({
        message: 'Error loading products',
        description: error.message || 'Failed to load available products'
      });
    }
  };

  // Handle quantity change for existing items
  const handleQuantityChange = (value, record) => {
    const newItems = orderItems.map(item => {
      if (item.key === record.key) {
        const allowDecimal = isCleaningQtyItem(record);
        const newQty = clampAndNormalizeQty(value, { allowDecimal });
        return {
          ...item,
          qty: newQty,
          order_products_qty: newQty
        };
      }
      return item;
    });
    
    setOrderItems(newItems);
    setHasChanges(true);
  };

  // Handle price change for existing items
  const handlePriceChange = (value, key) => {
    const newItems = orderItems.map(item => {
      if (item.key === key) {
        const newPrice = parseFloat(value) || 0;
        return {
          ...item,
          price: newPrice,
          order_products_price: newPrice
        };
      }
      return item;
    });
    
    setOrderItems(newItems);
    setHasChanges(true);
  };

  // Handle item removal
  const handleRemoveItem = (key) => {
    console.log('Removing item with key:', key);
    console.log('Current order items:', orderItems);  
    const newItems = orderItems.filter(item => item.key !== key);
    console.log('New order items:', newItems);
    setOrderItems(newItems);
    setHasChanges(true);
  };

  // Handle new product selection
  const handleProductSelect = (productId) => {
    const product = availableProducts.find(p => p.id == productId);
    
    if (product) {
      setNewItem({
        ...newItem,
        product_id: productId,
        product_name: product.product_title,
        price: parseFloat(product.product_price) || 0
      });
    }
  };

  // Handle new product quantity change
  const handleNewItemQtyChange = (value) => {
    const selectedProduct = availableProducts.find(p => p.id == newItem.product_id);
    const allowDecimal = isCleaningQtyItem({
      ...selectedProduct,
      product_id: selectedProduct?.id,
      product_title: selectedProduct?.product_title
    });

    setNewItem({
      ...newItem,
      qty: clampAndNormalizeQty(value, { allowDecimal })
    });
  };

  // Handle new product price change
  const handleNewItemPriceChange = (value) => {
    setNewItem({
      ...newItem,
      price: parseFloat(value) || 0
    });
  };

  // Add new item to order
  const handleAddItem = () => {
    if (!newItem.product_id) {
      notification.warning({
        message: 'Please select a product',
        description: 'You must select a product before adding it to the order'
      });
      return;
    }
    
    // Generate a unique key for the new item
    const key = `new-${Date.now()}`;
    
    const product = availableProducts.find(p => p.id == newItem.product_id);
    
    const newItemToAdd = {
      key,
      order_id: orderId,
      product_id: newItem.product_id,
      product_name: newItem.product_name,
      order_products_name: newItem.product_name,
      qty: newItem.qty,
      order_products_qty: newItem.qty,
      price: newItem.price,
      order_products_price: newItem.price,
      is_new: true,
      product_code: product ? product.product_code : '',
      product_image: product ? product.image : ''
    };
    
    setOrderItems([...orderItems, newItemToAdd]);
    
    // Reset new item form
    setNewItem({
      product_id: null,
      product_name: '',
      qty: 1,
      price: 0
    });
    
    setHasChanges(true);
  };
  
  // Add extra item to order
  const handleAddExtraItem = () => {
    if (!extraItem.name) {
      notification.warning({
        message: 'Please enter a name',
        description: 'You must enter a name for the extra item'
      });
      return;
    }
    
    // Generate a unique key for the extra item
    const key = `extra-${Date.now()}`;
    
    const extraItemToAdd = {
      key,
      order_id: orderId,
      product_id: null,
      product_name: extraItem.name,
      order_products_name: extraItem.name,
      qty: extraItem.qty,
      order_products_qty: extraItem.qty,
      price: extraItem.price,
      order_products_price: extraItem.price,
      is_new: true,
      is_extra: true,
      add_to_products: extraItem.add_to_products,
      product_code: 'EXTRA',
      product_image: null
    };
    
    setOrderItems([...orderItems, extraItemToAdd]);
    
    // Reset extra item form
    setExtraItem({
      name: '',
      qty: 1,
      price: 0,
      add_to_products: false
    });
    
    setShowExtraItem(false);
    setHasChanges(true);
  };

  // Handle discount value change
  const handleDiscountValueChange = (value) => {
    setDiscountValue(parseFloat(value) || 0);
    setHasChanges(true);
  };
  
  // Handle discount type change
  const handleDiscountTypeChange = (e) => {
    setDiscountType(e.target.value);
    setHasChanges(true);
  };
  
  // Toggle discount section
  const toggleDiscountSection = (e) => {
    setShowDiscountSection(e.target.checked);
    if (!e.target.checked) {
      setDiscountValue(0);
    }
    setHasChanges(true);
  };

  // Calculate subtotal
  const calculateSubtotal = () => {
    return calculateOrderEditTotals({ items: orderItems }).subtotal;
  };

  const orderShipping = resolveOrderShipping(order || {});
  
  // Calculate discount (if applicable)
  const calculateDiscount = () => {
    return calculateOrderEditTotals({
      items: orderItems,
      applyDiscount: showDiscountSection,
      discountType,
      discountValue,
    }).discount;
  };
  
  // Calculate final total
  const calculateTotal = () => {
    return calculateOrderEditTotals({
      items: orderItems,
      applyDiscount: showDiscountSection,
      discountType,
      discountValue,
      shipping: orderShipping,
      isCollection: Number(order?.order_collection_id || 0) > 0,
    }).total;
  };

  // Save changes
  const handleSave = async () => {
    if (!userRoleUtils.isAdminOrImpersonating()) {
      notification.error({
        message: 'Permission Denied',
        description: 'Only administrators can edit order items'
      });
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Prepare updated items data
      const updatedItems = orderItems.map(item => ({
        id: item.order_products_id, // Will be null for new items
        product_id: item.product_id,
        order_products_name: item.product_name,
        order_products_qty: item.qty,
        order_products_price: item.price,
        is_new: item.is_new || false,
        is_extra: item.is_extra || false,
        add_to_products: item.add_to_products || false
      }));
      
      const originalItems = order.order_products || [];
      const currentItemIds = orderItems
        .filter(item => item.order_products_id) // Only consider items with ids
        .map(item => item.order_products_id);
      
      const removedItemIds = originalItems
        .filter(item => item.order_products_id) // Only consider items with ids
        .map(item => item.order_products_id)
        .filter(order_products_id => !currentItemIds.includes(order_products_id));
      // Prepare payload
      const payload = {
        order_id: orderId,
        items: updatedItems,
        removed_items: removedItemIds,
        recalculate_total: true,
        apply_discount: showDiscountSection,
        discount_value: discountValue,
        discount_type: discountType,
        admin_notes: orderNote
      };
      
      // Call API to update order items
      // For testing, we can use the updateOrder method as a fallback if updateOrderItems doesn't exist yet
      if (typeof ordersService.updateOrderItems === 'function') {
        await ordersService.updateOrderItems(orderId, payload);
      } else {
        // Fallback to regular update method
        await ordersService.updateOrder(orderId, { items: payload });
      }
      
      notification.success({
        message: 'Order items updated',
        description: 'The order items have been successfully updated'
      });
      
      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal
      onCancel();
      
    } catch (error) {
      notification.error({
        message: 'Failed to update order items',
        description: error.message || 'An error occurred while updating order items'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Product',
      dataIndex: 'order_products_name',
      width: '30%',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.product_code && (
            <div className="text-xs text-gray-500">
              Code: {record.product_code}
            </div>
          )}
          {record.is_extra && (
            <Text type="warning" className="text-xs">
              (Extra Item)
            </Text>
          )}
          {record.is_new && !record.is_extra && (
            <Text type="success" className="text-xs">
              (New Item)
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'qty',
      width: '20%',
      render: (qty, record) => (
        <InputNumber
          min={isCleaningQtyItem(record) ? 0.05 : 1}
          step={cleaningQuantityStep(record, isCleaningQtyItem(record) ? 0.05 : 1)}
          precision={isCleaningQtyItem(record) ? 2 : 0}
          defaultValue={qty}
          value={qty}
          onChange={(value) => handleQuantityChange(value, record)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      width: '20%',
      render: (price, record) => (
        <InputNumber
          min={0}
          step={0.01}
          defaultValue={price}
          value={price}
          onChange={(value) => handlePriceChange(value, record.key)}
          style={{ width: '100%' }}
          prefix="£"
        />
      )
    },
    {
      title: 'Total',
      dataIndex: 'total',
      width: '20%',
      render: (_, record) => {
        const itemTotal = record.qty * record.price;
        return `£${formatPrice(itemTotal)}`;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(record.key)}
        />
      )
    }
  ];

  return (
    <Modal
      title={
        <div className="flex items-center">
          <ShoppingCartOutlined className="mr-2 text-blue-500" /> 
          Edit Order Items (Order #{orderId})
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!hasChanges}
          className="bg-golden-navy"
        >
          Save Changes
        </Button>
      ]}
    >
      <Spin spinning={isLoading}>
        {order ? (
          <div>
            <div className="bg-blue-50 p-4 rounded mb-4">
              <Text strong>Customer:</Text> {order.delivery_first_name} {order.delivery_last_name}
              <div className="text-sm text-gray-500">
                <Text strong>Delivery Date:</Text> {new Date(order.property_date).toLocaleDateString()}
              </div>
              <div className="text-sm text-gray-500">
                <Text strong>Current Total:</Text> £{formatPrice(order.order_total)}
              </div>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded flex items-center mb-4">
              <WarningOutlined className="text-yellow-500 mr-2" />
              <Text className="text-sm">
                Changing order items will recalculate the order total and may affect payment status.
              </Text>
            </div>
            
            <Table
              columns={columns}
              dataSource={orderItems}
              rowKey="key"
              pagination={false}
              scroll={{ y: 300 }}
              className="mb-4"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3} className="text-right">
                      <Text>Subtotal:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} colSpan={2}>
                      <Text>£{formatPrice(calculateSubtotal())}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  
                  {calculateDiscount() > 0 && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3} className="text-right">
                        <Text type="secondary">Discount:</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} colSpan={2}>
                        <Text type="secondary">- £{formatPrice(calculateDiscount())}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                  
                  {orderShipping > 0 && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3} className="text-right">
                        <Text type="secondary">Delivery Fee:</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} colSpan={2}>
                        <Text type="secondary">+ £{formatPrice(orderShipping)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                  
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3} className="text-right">
                      <Text strong>Total:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} colSpan={2}>
                      <Text strong>£{formatPrice(calculateTotal())}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
            
            <Divider>
              <Space>
                Discount Options
                <Tooltip title="Apply a discount to this order. You can choose between a fixed amount or percentage.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            </Divider>
            
            <div className="mb-6">
              <Checkbox 
                checked={showDiscountSection} 
                onChange={toggleDiscountSection}
                className="mb-3"
              >
                Apply Discount
              </Checkbox>
              
              {showDiscountSection && (
                <div className="bg-gray-50 p-4 rounded">
                  <div className="flex flex-wrap items-center gap-4">
                    <Radio.Group 
                      value={discountType} 
                      onChange={handleDiscountTypeChange}
                      buttonStyle="solid"
                    >
                      <Radio.Button value="fixed">
                        <PoundOutlined /> Fixed Amount
                      </Radio.Button>
                      <Radio.Button value="percentage">
                        <PercentageOutlined /> Percentage
                      </Radio.Button>
                    </Radio.Group>
                    
                    <InputNumber
                      min={0}
                      step={discountType === 'fixed' ? 1 : 0.5}
                      value={discountValue}
                      onChange={handleDiscountValueChange}
                      prefix={discountType === 'fixed' ? '£' : ''}
                      suffix={discountType === 'percentage' ? '%' : ''}
                      style={{ width: 150 }}
                    />
                    
                    <div className="ml-auto text-right">
                      <Text type="secondary">Discount Amount:</Text>
                      <div>
                        <Text strong>£{formatPrice(calculateDiscount())}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <Divider>Admin Notes</Divider>
            
            <div className="mb-6">
              <Input.TextArea
                placeholder="Add admin notes about this order here..."
                value={orderNote}
                onChange={(e) => {
                  setOrderNote(e.target.value);
                  setHasChanges(true);
                }}
                rows={3}
              />
              <Text type="secondary" className="mt-1 block">
                These notes are only visible to administrators.
              </Text>
            </div>
            
            <Divider>Add New Item</Divider>
            
            <div className="mb-4 flex gap-2">
              <Button 
                type={!showExtraItem ? 'primary' : 'default'}
                onClick={() => setShowExtraItem(false)}
              >
                Add from Products
              </Button>
              <Button 
                type={showExtraItem ? 'primary' : 'default'}
                onClick={() => setShowExtraItem(true)}
              >
                Add Extra Item
              </Button>
            </div>
            
            {!showExtraItem ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="md:col-span-2">
                  <Select
                    placeholder="Select Product"
                    style={{ width: '100%' }}
                    showSearch
                    filterOption={(input, option) => {
                      const children = option.children;
                      if (typeof children === 'string') {
                        return children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }
                      // Handle cases where children is not a string (like React elements)
                      return JSON.stringify(children).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    value={newItem.product_id}
                    onChange={handleProductSelect}
                  >
                    {availableProducts.map(product => (
                      <Option key={product.id} value={product.id}>
                        {product.product_title} (£{formatPrice(product.product_price)})
                      </Option>
                    ))}
                  
                  </Select>
                </div>
                
                <InputNumber
                  placeholder="Qty"
                  min={(() => {
                    const selectedProduct = availableProducts.find(p => p.id == newItem.product_id);
                    return isCleaningQtyItem({
                      ...selectedProduct,
                      product_id: selectedProduct?.id,
                      product_title: selectedProduct?.product_title
                    })
                      ? 0.05
                      : 1;
                  })()}
                  step={(() => {
                    const selectedProduct = availableProducts.find(p => p.id == newItem.product_id);
                    return cleaningQuantityStep(selectedProduct, isCleaningQtyItem(selectedProduct) ? 0.05 : 1);
                  })()}
                  precision={(() => {
                    const selectedProduct = availableProducts.find(p => p.id == newItem.product_id);
                    return isCleaningQtyItem({
                      ...selectedProduct,
                      product_id: selectedProduct?.id,
                      product_title: selectedProduct?.product_title
                    })
                      ? 2
                      : 0;
                  })()}
                  value={newItem.qty}
                  onChange={handleNewItemQtyChange}
                  style={{ width: '100%' }}
                />
                
                <InputNumber
                  placeholder="Price"
                  min={0}
                  step={0.01}
                  value={newItem.price}
                  onChange={handleNewItemPriceChange}
                  style={{ width: '100%' }}
                  prefix="£"
                />
                
                <div className="col-span-1 md:col-span-4 flex justify-end">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddItem}
                    disabled={!newItem.product_id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Add Item
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Enter custom item name"
                      value={extraItem.name}
                      onChange={(e) => setExtraItem({ ...extraItem, name: e.target.value })}
                    />
                  </div>
                  
                  <InputNumber
                    placeholder="Qty"
                    min={1}
                    value={extraItem.qty}
                    onChange={(value) => setExtraItem({ ...extraItem, qty: value })}
                    style={{ width: '100%' }}
                  />
                  
                  <InputNumber
                    placeholder="Price"
                    min={0}
                    step={0.01}
                    value={extraItem.price}
                    onChange={(value) => setExtraItem({ ...extraItem, price: value })}
                    style={{ width: '100%' }}
                    prefix="£"
                  />
                  
                  <div className="col-span-1 md:col-span-4">
                    <Checkbox
                      checked={extraItem.add_to_products}
                      onChange={(e) => setExtraItem({ ...extraItem, add_to_products: e.target.checked })}
                    >
                      Add to products catalog
                    </Checkbox>
                  </div>
                  
                  <div className="col-span-1 md:col-span-4 flex justify-end">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddExtraItem}
                      disabled={!extraItem.name}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Add Extra Item
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Spin tip="Loading order data..." />
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default OrderItemsEditModal;
